import React from 'react';
import { Link } from 'react-router-dom';
import { Factory, Flame, Droplet, Shield, Mountain, Fish, Recycle, ArrowRight, Zap, Layers } from 'lucide-react';

const Industries = () => {
  const industries = [
    {
      icon: <Factory className="w-12 h-12" />,
      title: 'Power Plants',
      description: 'Extensive knowledge of Alaska power generation regulations and compliance requirements. Due to Alaska\'s vast and remote geography, we understand tailored regulations addressing unique challenges in rural areas.',
      color: 'from-blue-600 to-blue-700',
      link: '/sectors/power-plants',
    },
    {
      icon: <Flame className="w-12 h-12" />,
      title: 'Coal-Fired Power Plants',
      description: 'Coal-fired power plants are integral to Alaska\'s energy landscape. We provide expertise in stringent regulatory air quality permitting, implementing robust frameworks that balance energy needs with environmental responsibilities.',
      color: 'from-gray-700 to-gray-800',
      link: '/sectors/coal-fired-power-plants',
    },
    {
      icon: <Droplet className="w-12 h-12" />,
      title: 'Oil & Gas Development',
      description: 'Maintaining air quality in oil and gas is paramount for regulatory compliance and community health. Our extensive expertise ensures operations align with industry standards and regulatory requirements.',
      color: 'from-amber-600 to-amber-700',
      link: '/sectors/oil-and-gas',
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: 'Military Installations',
      description: 'Specializing in air quality control for military installations, ensuring armed forces operate in environments prioritizing health and performance. Dedicated to supporting servicemen and women with superior solutions.',
      color: 'from-green-700 to-green-800',
      link: '/sectors/military-installations',
    },
    {
      icon: <Mountain className="w-12 h-12" />,
      title: 'Mining & Road Construction',
      description: 'Committed to providing state-of-the-art permit assistance for mining operations. Comprehensive services including fugitive dust mitigation plans, rock crushing, and asphalt production permits.',
      color: 'from-orange-600 to-orange-700',
      link: '/sectors/mining-construction',
    },
    {
      icon: <Fish className="w-12 h-12" />,
      title: 'Seafood Processing Plants',
      description: 'Air quality is critical in seafood processing, particularly for fish drying and power generation. We manage VOCs and particulate matter emissions to ensure compliance and protect worker health.',
      color: 'from-cyan-600 to-cyan-700',
      link: '/sectors/seafood-processing',
    },
    {
      icon: <Recycle className="w-12 h-12" />,
      title: 'Soil Remediation & Thermal Incinerators',
      description: 'Expert in soil remediation aimed at restoring contaminated land. Specializing in PFAS and other pollutants management, promoting ecosystem restoration and improved land value.',
      color: 'from-emerald-600 to-emerald-700',
      link: '/sectors/soil-remediation',
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: 'Geothermal Test Well Permitting',
      description: 'Comprehensive permitting support for geothermal exploration and test well operations. Expert assistance with H2S management, air quality compliance, and renewable energy development in Alaska.',
      color: 'from-purple-600 to-purple-700',
      link: '/sectors/geothermal-permitting',
    },
    {
      icon: <Layers className="w-12 h-12" />,
      title: 'Antimony Roasting',
      description: 'Specialized air quality permitting for antimony roasting operations. Expert support for high SO₂ emissions control, NSPS compliance, hazardous air pollutant management, and Title V permitting for metal roasting facilities.',
      color: 'from-slate-600 to-slate-700',
      link: '/sectors/antimony-roasting',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="industries-page">
      {/* Hero Section */}
      <section
        className="relative bg-gradient-to-r from-emerald-900 to-emerald-700 text-white py-20"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://images.unsplash.com/photo-1640125346217-e4bb9313d6bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxhZXJpYWwlMjBmb3Jlc3R8ZW58MHx8fHwxNzYxMTAxMTkxfDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="page-title">
            Industries We Serve
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl">
            Specialized environmental consulting tailored to Alaska's unique conditions across diverse industries.
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 bg-white" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              In-Depth Knowledge of Environmental Management
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Raven Environmental Consulting possesses extensive knowledge of current regulations and compliance requirements specific to Alaska's diverse industrial landscape. Our expertise spans multiple sectors, ensuring your operations meet all environmental standards.
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 bg-gray-50" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <Link
                key={index}
                to={industry.link}
                data-testid={`industry-card-${index}`}
                className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group cursor-pointer flex flex-col"
              >
                <div className={`bg-gradient-to-r ${industry.color} p-8 text-white flex-shrink-0 min-h-[200px] flex flex-col justify-center`}>
                  <div className="w-16 h-16 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    {industry.icon}
                  </div>
                  <h3 className="text-2xl font-bold">{industry.title}</h3>
                </div>
                <div className="p-6 flex-grow flex flex-col">
                  <p className="text-gray-600 leading-relaxed mb-4 flex-grow">{industry.description}</p>
                  <span className="text-sm font-semibold text-gray-900 group-hover:text-emerald-700 transition-colors">
                    Learn More →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-emerald-700 text-white" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Partner With Us?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Choose Raven Environmental Consulting to ensure superior environmental compliance for your industry. We're dedicated to supporting your operations with the best solutions available.
          </p>
          <Link
            to="/contact"
            data-testid="cta-button"
            className="inline-flex items-center px-8 py-4 bg-white text-emerald-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
          >
            Contact Us Today
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Industries;